/**
 * @file     formatLotSize.ts
 * @purpose  Render a lot area in acres when large enough to read that way,
 *           square feet otherwise. Single formatting rule for every surface.
 */

const SQFT_PER_ACRE = 43_560;
/** Switch to acres at half an acre; below that sqft reads better. */
const ACRE_DISPLAY_THRESHOLD_SQFT = SQFT_PER_ACRE / 2;

export interface FormattedLotSize {
  /** e.g. "2.47" or "9,690" */
  value: string;
  /** 'acres' | 'sqft' — callers pick their own localized unit label. */
  unit: 'acres' | 'sqft';
}

export function formatLotSizeParts(sqft: number | null | undefined): FormattedLotSize | null {
  if (sqft == null || !Number.isFinite(sqft) || sqft <= 0) return null;
  if (sqft >= ACRE_DISPLAY_THRESHOLD_SQFT) {
    const acres = sqft / SQFT_PER_ACRE;
    const value = acres >= 10 ? acres.toFixed(1) : acres.toFixed(2);
    return { value, unit: 'acres' };
  }
  return { value: Math.round(sqft).toLocaleString('en-CA'), unit: 'sqft' };
}

/** Convenience for plain-text contexts: "2.47 acres" / "9,690 ft²". */
export function formatLotSize(
  sqft: number | null | undefined,
  labels: { acres?: string; sqft?: string } = {},
): string | null {
  const parts = formatLotSizeParts(sqft);
  if (!parts) return null;
  const unit = parts.unit === 'acres' ? (labels.acres ?? 'acres') : (labels.sqft ?? 'ft²');
  return `${parts.value} ${unit}`;
}
